import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import * as moment from 'moment';

import { ConfigService } from './config.service';
import { ShiftRange } from '../models/shift-range.model';
import { ShiftBlock } from '../models/shift-block.model';
import { MissingBlock } from '../models/missing-block.model';
import { ShiftSchedule } from '../models/shift-schedule.model';
import { MissingUosBlock } from '../models/missing-uos-block.model';
import { WorkflowConstants } from 'src/app/workflow/workflow-constants.util';
import { Constants } from '../utilities/constants.util';
import { MiscUtil } from '../utilities/misc.util';
import { ShiftBlockDay } from '../models/shift-block-day.model';

@Injectable({
    providedIn: 'root'
})
export class ShiftBlockService {
    readonly apiServer: string;

    constructor(private _configService: ConfigService, private _http: HttpClient) {
      this.apiServer = this._configService.appSettings.webApiServer + "api/ShiftBlock/";
    }

    async getSchedulesForCostCenter(costCenterId: number) : Promise<ShiftSchedule[]> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString());
        return await this._http.get<ShiftSchedule[]>(`${this.apiServer}GetSchedules`, { params : params }).toPromise<ShiftSchedule[]>();
    }

    async scheduleShiftBlocks(schedules: ShiftSchedule[]): Promise<boolean> {
        return await this._http.post<boolean>(`${this.apiServer}ScheduleShiftBlocks`, schedules).toPromise<boolean>();
    }

    async getShiftBlocksbyCostCenterIds(costCenterIds:number[]): Promise<ShiftRange[]> {
        const params = new HttpParams()
            .set('request', JSON.stringify(costCenterIds));

        return await this._http.get<ShiftRange[]>(`${this.apiServer}GetShiftBlocksbyCostCenterIds`, { params: params }).toPromise<ShiftRange[]>();
    }

    async getShiftBlocksForCostCenter(costCenterId: number): Promise<ShiftBlock[]> {
        const params = new HttpParams()
            .set('costCenterId', costCenterId.toString());

        return await this._http.get<ShiftBlock[]>(`${this.apiServer}GetShiftBlocksForCostCenter`, { params : params }).toPromise<ShiftBlock[]>();
    }

    loadShiftBlockById(id: number) : Promise<ShiftBlock> {
        const params = new HttpParams().set('shiftBlockId', id.toString());

        return this._http.get<ShiftBlock>(`${this.apiServer}GetShiftBlockById`, { params : params }).toPromise();
    }
    
    loadShiftBlock(costCenterId: number|string, workflowTime: string) : Promise<ShiftBlock> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('timestamp', workflowTime);

        return this._http.get<ShiftBlock>(`${this.apiServer}GetShiftBlock`, { params : params }).toPromise();
    }
    
    loadCurrentShiftBlock(costCenterId: number|string) : Promise<ShiftBlockDay> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString());

        return this._http.get<ShiftBlockDay>(`${this.apiServer}GetCurrentShiftBlock`, { params : params }).toPromise();
    }

    getPreviousShiftBlockForDate(costCenterId: number|string, workflowTimeFacility: string) : Promise<ShiftBlock> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('workflowTimeFacility', workflowTimeFacility);

        return this._http.get<ShiftBlock>(`${this.apiServer}GetPreviousShiftBlockForDate`, { params : params }).toPromise();
    }

   getPreviousShiftBlockDay(costCenterId: number|string, workflowTimeFacility: string) : Promise<ShiftBlockDay> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('workflowTimeFacility', workflowTimeFacility);

        return this._http.get<ShiftBlockDay>(`${this.apiServer}GetPreviousShiftBlockDay`, { params : params }).toPromise();
    }

    loadList(costCenterId: number|string, date: string) : Promise<ShiftBlock[]> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('date', date);
    
        return this._http.get<ShiftBlock[]>(`${this.apiServer}GetShiftBlocks`, { params : params }).toPromise();
    }

    loadMissingBlocks(costCenterId: number|string, numberOfDays: number) {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('numberOfDays', numberOfDays.toString());
                                       
        return this._http.get<MissingBlock[]>(`${this.apiServer}GetMissingBlocks`, { params : params }).toPromise();
    }

    loadMissingUosBlocks(costCenterId: number, numberOfDays: number): Promise<MissingUosBlock[]> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('numberOfDays', numberOfDays.toString());

        return this._http.get<MissingUosBlock[]>(`${this.apiServer}GetMissingUosBlocks`, { params : params }).toPromise<MissingUosBlock[]>();
    }

    async findPreviousUosWorkingDay(costCenterId: number, workflowTimeFacility: string): Promise<Date> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString())
                                       .set('workflowTimeFacility', workflowTimeFacility);

        return this._http.get<Date>(`${this.apiServer}FindPreviousWorkingDay`, { params: params}).toPromise<Date>();
    }

    formatShiftTime(shiftBlock, workflowTime) {
        return this.formatShiftTimeWithFormat(
            shiftBlock, 
            workflowTime, 
            WorkflowConstants.MomentFormat + ' (ha)', 
            WorkflowConstants.MomentFormat +' (ha', 
            ' - ha)');
    }

    formatShiftTimeHoursOnly(shiftBlock, workflowTime): string {
        return this.formatShiftTimeWithFormat(shiftBlock, workflowTime, 'ha', 'ha', '- ha');
    }

    formatShiftTimeWithFormat(shiftBlock, workflowTime, daySpanFormat, startStringFormat, endStringFormat) {
        if (!shiftBlock || !workflowTime) return '';

        let start = this.getShiftBlockStartTime(shiftBlock, workflowTime);
        let end = this.getShiftBlockEndTime(shiftBlock, workflowTime);

        let startString ='';
        let endString = ''

        if (start.day() != end.day()){
            startString = start.format(daySpanFormat);
            endString = end.format(" - " + daySpanFormat);
        } else {
            startString = start.format(startStringFormat);
            endString = end.format(endStringFormat);
        }

        return `${startString}${endString}`;
    }

    getShiftBlockStartTime(shiftBlock: ShiftBlock, workflowTime): moment.Moment {
        return moment(workflowTime).hour(shiftBlock.startHour);
    }

    getShiftBlockEndTime(shiftBlock: ShiftBlock, workflowTime): moment.Moment {
        let end = moment(workflowTime).hour(shiftBlock.endHour);

        if (shiftBlock.endHour <= shiftBlock.startHour) {
            end = end.add(1, 'days');
        }
        return end;
    }
}
